import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FaLandmark,
  FaFileInvoice,
  FaUniversity,
  FaWallet,
  FaCheck,
  FaArrowRight,
} from "react-icons/fa";

import Header from "../Templete/Header";
import Footer from "../Templete/Footer";
import "./ServicePages.css";

const services = [
  {
    title: "E-Governance Services",
    icon: FaLandmark,
    description:
      "Digital service workflows that help customers and service operators manage applications and document-based requests.",
    features: [
      "Application and document collection interfaces",
      "PAN service workflows",
      "Service operator dashboards and status tracking",
    ],
  },
  {
    title: "Bill Payment Service",
    icon: FaFileInvoice,
    description:
      "Bill payment journeys that connect customer requests, bill details, payment processing, and transaction updates.",
    features: [
      "Bill fetch and payment integrations",
      "Utility and supported biller workflows",
      "Transaction tracking and reporting tools",
    ],
  },
  {
    title: "Banking Business Correspondent Service",
    icon: FaUniversity,
    description:
      "Technology and service workflows for banking correspondent operations through applicable partner arrangements.",
    features: [
      "Agent-facing service applications",
      "Integration with authorised partner services",
      "Transaction records and operational reporting",
    ],
  },
  {
    title: "Payment Services",
    icon: FaWallet,
    description:
      "Payment technology and integrations that connect business applications with their payment service partners.",
    features: [
      "Payment gateway and collection integrations",
      "Webhook and transaction status workflows",
      "Reconciliation and payment reporting tools",
    ],
  },
];

const PaymentsEGovernance = () => {
  useEffect(() => {
    document.title = "Payments & E-Governance Services | Formax IT Solutions";
  }, []);

  return (
    <>
      <Header />

      <main className="fit-services fit-services--payments">
        <section className="fit-services__hero">
          <div className="fit-services__container">
            <nav className="fit-services__breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span aria-hidden="true">/</span>
              <span aria-current="page">Payments & E-Governance</span>
            </nav>

            <span className="fit-services__eyebrow">
              CONNECTED DIGITAL SERVICES
            </span>

            <h1>
              Payments &<span>E-Governance Services</span>
            </h1>

            <p>
              Connect businesses and customers with payment, bill payment,
              banking correspondent, and document-based digital service
              workflows.
            </p>

            <a href="#payment-services" className="fit-services__button">
              Explore our services
              <FaArrowRight aria-hidden="true" />
            </a>
          </div>
        </section>

        <section className="fit-services__section">
          <div className="fit-services__container fit-services__intro">
            <div>
              <span className="fit-services__eyebrow">
                CONNECTING SERVICES & OPERATIONS
              </span>
              <h2>Build a clearer journey for every service request.</h2>
            </div>
            <div>
              <p>
                Digital services bring together customer information, external
                integrations, transaction activity, and operational follow-up.
                We help structure these elements into practical application
                workflows.
              </p>
              <p>
                Our work considers both the customer experience and the tools
                teams need to manage requests, track updates, and investigate
                exceptions.
              </p>
            </div>
          </div>
        </section>

        <section
          id="payment-services"
          className="fit-services__section fit-services__section--soft"
          aria-labelledby="payment-services-title"
        >
          <div className="fit-services__container">
            <div className="fit-services__heading">
              <span className="fit-services__eyebrow">OUR SERVICES</span>
              <h2 id="payment-services-title">
                Connected capabilities for everyday services.
              </h2>
              <p>
                Applications and integrations shaped around your service model
                and partner requirements.
              </p>
            </div>

            <div className="fit-services__grid fit-services__grid--two">
              {services.map((service) => {
                const Icon = service.icon;

                return (
                  <article className="fit-services__card" key={service.title}>
                    <span className="fit-services__icon">
                      <Icon aria-hidden="true" />
                    </span>
                    <h3>{service.title}</h3>
                    <p>{service.description}</p>
                    <ul className="fit-services__list">
                      {service.features.map((feature) => (
                        <li key={feature}>
                          <FaCheck aria-hidden="true" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="fit-services__section">
          <div className="fit-services__container fit-services__platform">
            <div>
              <span className="fit-services__eyebrow">FORMAX PAY</span>
              <h2>Our focus on payment technology.</h2>
              <p>
                Through Formax Pay, we are building API infrastructure and
                payment orchestration capabilities for business partners. Our
                focus connects application development with payment integration
                and operational requirements.
              </p>
            </div>

            <div className="fit-services__platform-panel">
              <h3>What our approach considers</h3>
              <ul className="fit-services__list">
                {[
                  "Defined integration contracts and response handling",
                  "Transaction updates through webhooks and status checks",
                  "Operational visibility and reconciliation workflows",
                  "Partner-specific onboarding and service requirements",
                ].map((item) => (
                  <li key={item}>
                    <FaCheck aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="fit-services__section fit-services__section--soft">
          <div className="fit-services__container">
            <div className="fit-services__heading">
              <span className="fit-services__eyebrow">GETTING STARTED</span>
              <h2>Define the service before the integration.</h2>
            </div>

            <ol className="fit-services__steps">
              {[
                [
                  "Understand the model",
                  "Identify the services, users, and operating workflows.",
                ],
                [
                  "Review requirements",
                  "Map partner documentation, onboarding, and integration needs.",
                ],
                [
                  "Integrate & validate",
                  "Develop the required journeys and check partner responses.",
                ],
                [
                  "Prepare operations",
                  "Plan reporting, exception handling, and release readiness.",
                ],
              ].map(([title, text], index) => (
                <li key={title}>
                  <span aria-hidden="true">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="fit-services__section">
          <div className="fit-services__container">
            <div className="fit-services__cta">
              <div>
                <span className="fit-services__eyebrow">LET’S CONNECT</span>
                <h2>Share your service requirements.</h2>
                <p>
                  Talk to us about your payment integration, digital service
                  platform, or partnership enquiry.
                </p>
              </div>
              <Link to="/contact-us" className="fit-services__button">
                Contact us <FaArrowRight aria-hidden="true" />
              </Link>
            </div>

            <Link
              to="/technology-digital-solutions"
              className="fit-services__related"
            >
              Explore Technology & Digital Solutions
              <FaArrowRight aria-hidden="true" />
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default PaymentsEGovernance;
